import { ChronoUnit, Duration, LocalTime } from "@js-joda/core";
import * as React from "react";
import { DistanceRequest } from "../GeoService";
import { CaregiverImportedVisitDetails, VisitDayTime, VisitDayTimeParams } from "../messages/Visit";
import { STRINGS } from "../strings";
import { decimalHourToDisplay } from "../time";
import { globalize, translate } from "../translate";
import { DistanceLabel } from "./DistanceLabel";

export function ChatBadge(props: {
  chatCount: number | undefined;
  isAssigned: boolean;
  viewed: boolean;
  accepted: boolean;
}) {
  if (props.chatCount !== undefined && props.chatCount > 0) {
    return <span />;
  }
  return <span />;
}

// Currently we have no recurring + split.
export function isConsistent(recurringVisitDayTimes: VisitDayTime[]): boolean {
  const daysShifts = dayShiftsFromDayTimeParams(recurringVisitDayTimes);
  const dayLengths: number[] = [];

  daysShifts.forEach((dayShifts) => {
    dayLengths.push(
      dayShifts.reduce((dayTotalHours, shift) => {
        const shiftMinutes = shift.startTime.until(shift.endTime, ChronoUnit.MINUTES);
        const actualShiftMinutes = (shiftMinutes + 60 * 24) % (60 * 24);

        return dayTotalHours + actualShiftMinutes;
      }, 0)
    );
  });

  return dayLengths.length === 0
    ? true
    : dayLengths.every((dayLength) => dayLength === dayLengths[0]);
}

export function getDayDisplayName(dayNumber: number): string {
  switch (dayNumber) {
    case 0:
      return "Sunday";
    case 1:
      return "Monday";
    case 2:
      return "Tuesday";
    case 3:
      return "Wednesday";
    case 4:
      return "Thursday";
    case 5:
      return "Friday";
    case 6:
      return "Saturday";
    default:
      throw new Error("The Impossible Happened");
  }
}

export function getConsistentRecurringVisitDisplayTime(recurringVisitDayTimes: VisitDayTime[]) {
  const firstDayShifts = recurringVisitDayTimes.filter(
    (days) => days.day === recurringVisitDayTimes[0].day
  );
  const dayLength = firstDayShifts.reduce((hoursExact, shift) => {
    let delta = Duration.between(shift.startTime, shift.endTime);
    if (delta.isZero() || delta.isNegative()) {
      delta = delta.plusDays(1);
    }
    return hoursExact + delta.seconds() / LocalTime.SECONDS_PER_HOUR;
  }, 0);

  return decimalHourToDisplay(dayLength);
}

// Simulates merging split visits to a single day for various algorithm that want to ignore shifts.
export function mergeVisitDayTimeParamsShifts(
  visitDayTimeParams: VisitDayTime[]
): VisitDayTimeParams {
  const dayShift = new Map<number, VisitDayTime>();
  const days: VisitDayTime[] = [];

  for (const shift of visitDayTimeParams) {
    if (!dayShift.has(shift.day)) {
      // Creates a copy to edit later.
      dayShift.set(shift.day, {
        day: shift.day,
        startTime: shift.startTime,
        endTime: shift.endTime,
      });
      days.push(dayShift.get(shift.day) as VisitDayTime);
    } else {
      const currShift = dayShift.get(shift.day) as VisitDayTime;
      if (currShift.startTime > shift.startTime) {
        currShift.startTime = shift.startTime;
      }
      if (currShift.endTime < shift.endTime) {
        currShift.endTime = shift.endTime;
      }
    }
  }

  return {
    dayTimes: days.sort((day1, day2) => day1.day - day2.day),
  };
}

// Returns shifts for every day (0 as SUNDAY, so on: DayOfWeek() % 7).
export function dayShiftsFromDayTimeParams(
  visitDayTimeParams: VisitDayTime[]
): Map<number, VisitDayTime[]> {
  const dayShiftsMap = new Map<number, VisitDayTime[]>();

  for (const shift of visitDayTimeParams) {
    if (!dayShiftsMap.has(shift.day)) {
      // Creates a copy to edit later.
      dayShiftsMap.set(shift.day, []);
    }

    const dayShifts = dayShiftsMap.get(shift.day) as VisitDayTime[];
    dayShifts.push(shift);
  }

  return dayShiftsMap;
}

export function HeaderMiles(props: {
  assigned: boolean;
  viewed: boolean;
  accepted: boolean;
  imported: boolean;
  distanceRequest: DistanceRequest | undefined;
}): JSX.Element {
  const headerBackground = getBackground(
    props.assigned,
    props.viewed,
    props.accepted,
    props.imported
  );
  let headerText = "";
  let textCss = "";
  if (props.assigned) {
    headerText = translate(STRINGS.ASSIGNED);
    textCss = "VisitListComponents__headerLeftTextAssigned";
  }
  if (props.imported) {
    headerText = translate(STRINGS.IMPORTED);
    textCss = "VisitListComponents__headerLeftTextAssigned";
  }

  return (
    <div className="VisitListComponents__headerRoot">
      <div className={"VisitListComponents__headerLeft " + headerBackground}>
        <div className={"VisitListComponents__headerLeftText " + textCss}>{headerText}</div>
        {props.assigned || props.imported ? null : (
          <div>
            {props.distanceRequest === undefined ? null : (
              <div className="VisitListComponents__headerLeftTextMiles">
                <DistanceLabel
                  distanceRequest={props.distanceRequest}
                  formatter={metersToMilesRounded}
                />
              </div>
            )}
            <div
              className={
                "VisitListComponents__headerLeftTextMiles VisitListComponents__headerLeftTextMilesDesc"
              }
            >
              {translate(STRINGS.MILES_AWAY)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getBackground(
  isAssigned: boolean,
  viewed: boolean,
  accepted: boolean,
  imported: boolean
): string {
  if (isAssigned || imported) {
    return "VisitListComponents__backgroundAssigned";
  } else if (accepted) {
    return "VisitListComponents__backgroundRequested";
  } else if (viewed) {
    return "VisitListComponents__backgroundViewed";
  } else {
    return "VisitListComponents__backgroundNew";
  }
}

export function ImportedVisitBody(props: { importedVisitDetails: CaregiverImportedVisitDetails }) {
  return (
    <div>
      <div className="VisitListComponents__bodyLeftHeader">
        <div className="bold">{translate(STRINGS.SINGLE_VISIT_FROM)}:</div>
        {props.importedVisitDetails.agencyName}
      </div>
      <div className="VisitListComponents__bodyLeftText">
        <span className="bold">{translate(STRINGS.VISIT_DATE)}: </span>
        {globalize().formatLocalDateTime(props.importedVisitDetails.startTimeLocal, {
          date: "medium",
        })}
      </div>
      <div className="VisitListComponents__bodyLeftText">
        <span className="bold">{translate(STRINGS.START_TIME)}: </span>
        {globalize().formatLocalDateTime(props.importedVisitDetails.startTimeLocal, {
          time: "short",
        })}
      </div>
      <div className="VisitListComponents__bodyLeftText">
        <span className="bold">{translate(STRINGS.END_TIME)}: </span>
        {globalize().formatLocalDateTime(props.importedVisitDetails.endTimeLocal, {
          time: "short",
        })}
      </div>
    </div>
  );
}

export function VisitFooter(props: {
  assigned: boolean;
  viewed: boolean;
  requested: boolean;
  isPreAssigned: boolean;
  cluster: boolean;
  onNotInterestedClick?: () => void;
  onMoreDetailsClick?: () => void;
}) {
  let border: string = "";
  if (props.assigned) {
    border = "VisitListComponents__footerAssignedBorder";
  } else if (props.requested) {
    border = "VisitListComponents__footerRequestedBorder";
  } else if (props.viewed) {
    border = "VisitListComponents__footerViewedBorder";
  } else {
    border = "VisitListComponents__footerNewBorder";
  }

  let clusterPadding = "";

  if (props.cluster) {
    clusterPadding = "VisitListComponents__clusterPadding";
  }

  if (props.assigned || props.requested) {
    return (
      <div className={`VisitListComponents__footer ${border}`}>
        <div className="VisitListComponents__footerButton">{translate(STRINGS.MORE_DETAILS)}</div>
      </div>
    );
  }

  return (
    <div className={`VisitListComponents__footer ${border} ${clusterPadding}`}>
      <div
        onClick={props.onNotInterestedClick}
        className="VisitListComponents__footerButtonNotInterested"
      >
        <div className={"VisitListComponents__footerInterestText"}>
          {props.isPreAssigned ? translate(STRINGS.REJECT) : translate(STRINGS.NOT_INTERESTED)}
        </div>
      </div>
      <div
        onClick={props.onMoreDetailsClick}
        className="VisitListComponents__footerButtonMoreDetails"
      >
        <div className={"VisitListComponents__footerInterestText"}>
          {props.isPreAssigned
            ? translate(STRINGS.READ_AND_CONFIRM)
            : translate(STRINGS.MORE_DETAILS)}
        </div>
      </div>
    </div>
  );
}

export function metersToMilesRounded(meters: number): number {
  const MILES_PER_METER = 0.000621371192;
  // the + gets rid of 0s after the .
  let roundedMiles = +(meters * MILES_PER_METER).toFixed(1);
  if (roundedMiles === 0) {
    roundedMiles = 0.1;
  }
  return roundedMiles;
}
